import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Play, Plus, Check, Star, Calendar, Clock3, User as UserIcon, Film, ShieldAlert, Users } from "lucide-react";
import { api, fileImageUrl } from "../lib/api";
import VideoPlayer from "../components/VideoPlayer";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function MovieDetail() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const { user } = useAuth();
  const [movie, setMovie] = useState(null);
  const [comments, setComments] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [inList, setInList] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [rating, setRating] = useState(5);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [{ data: m }, { data: c }] = await Promise.all([
        api.get(`/movies/${id}`),
        api.get(`/movies/${id}/comments`),
      ]);
      setMovie(m);
      setComments(c);
    } catch (err) {
      console.error(err);
    }
    if (user && user !== false) {
      try {
        const { data: ids } = await api.get("/favorites/ids");
        setInList(ids.includes(id));
      } catch (err) { console.error(err); }
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    if (search.get("play") === "1") setPlaying(true);
  }, [id, user]);

  const toggleFav = async () => {
    if (!user || user === false) return toast.error("Connectez-vous pour utiliser Ma Liste");
    try {
      if (inList) {
        await api.delete(`/favorites/${id}`);
        setInList(false);
        toast.success("Retiré");
      } else {
        await api.post(`/favorites/${id}`);
        setInList(true);
        toast.success("Ajouté à Ma Liste");
      }
    } catch (err) {
      console.error(err);
      toast.error("Action impossible");
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!user || user === false) return toast.error("Connectez-vous pour commenter");
    if (!commentText.trim()) return;
    try {
      const { data } = await api.post(`/movies/${id}/comments`, { text: commentText, rating });
      setComments([data, ...comments]);
      setCommentText("");
      toast.success("Commentaire publié");
      // reload movie for new avg
      const { data: m } = await api.get(`/movies/${id}`);
      setMovie(m);
    } catch (err) {
      console.error(err);
      toast.error("Erreur");
    }
  };

  if (loading) {
    return <div className="min-h-screen pt-32 text-center text-zinc-400">Chargement…</div>;
  }
  if (!movie) {
    return <div className="min-h-screen pt-32 text-center text-zinc-400">Film introuvable</div>;
  }

  const bg = fileImageUrl(movie.backdrop_path || movie.thumbnail_path);

  return (
    <div className="pb-24">
      {playing && <VideoPlayer movie={movie} onClose={() => setPlaying(false)} />}

      <div className="relative h-[70vh] min-h-[480px] overflow-hidden">
        {bg && <img src={bg} alt={movie.title} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 hero-scrim-side" />
        <div className="absolute inset-0 hero-scrim-bottom" />
        <div className="relative z-10 max-w-[1600px] mx-auto h-full px-4 sm:px-8 lg:px-12 flex items-end pb-16">
          <div className="space-y-6 max-w-3xl">
            <div className="flex items-center gap-3 flex-wrap">
              {movie.is_4k && (
                <span className="font-mono-mini text-[11px] px-3 py-1 border border-[color:var(--ns-accent)] text-[color:var(--ns-accent)] uppercase tracking-[0.3em]">
                  4K Ultra HD
                </span>
              )}
              {movie.is_hdr && (
                <span className="font-mono-mini text-[11px] px-3 py-1 border border-white/30 text-white uppercase tracking-[0.3em]">
                  HDR
                </span>
              )}
              {movie.is_dolby_atmos && (
                <span className="font-mono-mini text-[11px] px-3 py-1 border border-white/30 text-white uppercase tracking-[0.3em]">
                  Dolby Atmos
                </span>
              )}
              <span className="font-mono-mini text-[11px] px-3 py-1 border border-white/20 uppercase tracking-[0.3em]">
                {movie.genre}
              </span>
              <span className="font-mono-mini text-[11px] text-zinc-400 uppercase tracking-[0.3em]">
                {movie.year} · {movie.duration_min} min · {movie.rating}
              </span>
              {movie.avg_rating > 0 && (
                <span className="font-mono-mini text-[11px] text-yellow-400 flex items-center gap-1">
                  <Star size={12} fill="currentColor" /> {movie.avg_rating} ({movie.comments_count})
                </span>
              )}
            </div>
            <h1 className="font-display text-5xl sm:text-7xl uppercase leading-[0.95]">{movie.title}</h1>
            <p className="text-base text-zinc-300 max-w-2xl leading-relaxed">{movie.synopsis}</p>
            <div className="flex items-center gap-3">
              {movie.video_path ? (
                <button
                  data-testid="detail-play-button"
                  onClick={() => setPlaying(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 btn-accent rounded-full font-semibold text-sm uppercase tracking-widest"
                >
                  <Play size={16} fill="currentColor" /> Lecture
                </button>
              ) : (
                <span
                  data-testid="detail-coming-soon"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/15 rounded-full text-sm uppercase tracking-widest text-zinc-300"
                >
                  Bientôt disponible
                </span>
              )}
              <button
                data-testid="detail-fav-button"
                onClick={toggleFav}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/15 hover:bg-white/15 rounded-full text-sm uppercase tracking-widest transition"
              >
                {inList ? <Check size={16} /> : <Plus size={16} />} Ma Liste
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 mt-12 space-y-12">
        {/* Fiche technique - grands blocs */}
        <section data-testid="detail-fiche" className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <InfoTile icon={<Calendar size={16} />} label="Date de sortie" value={movie.year ? String(movie.year) : "—"} />
          <InfoTile icon={<Clock3 size={16} />} label="Durée" value={movie.duration_min ? `${movie.duration_min} min` : "—"} />
          <InfoTile icon={<ShieldAlert size={16} />} label="Classification" value={movie.rating || "TP"} accent />
          <InfoTile icon={<UserIcon size={16} />} label="Réalisation" value={movie.director || "—"} />
          <InfoTile icon={<Film size={16} />} label="Catégorie" value={movie.genre} />
        </section>

        <div className="grid md:grid-cols-3 gap-10">
          <aside className="space-y-6">
            <div>
              <div className="font-mono-mini text-[10px] uppercase tracking-[0.3em] text-[color:var(--ns-accent)] mb-2 flex items-center gap-2">
                <Users size={13} /> Casting
              </div>
              <ul className="space-y-2 text-sm text-zinc-200 leading-relaxed">
                {(movie.cast || "—").split(",").map((c, i) => (
                  <li key={i} className="pl-3 border-l border-white/10">
                    {c.trim()}
                  </li>
                ))}
              </ul>
            </div>

            {movie.tags && movie.tags.length > 0 && (
              <div>
                <div className="font-mono-mini text-[10px] uppercase tracking-[0.3em] text-[color:var(--ns-accent)] mb-2">
                  Ambiance
                </div>
                <div className="flex flex-wrap gap-2">
                  {movie.tags.map((t) => (
                    <span key={t} className="font-mono-mini text-[10px] px-2.5 py-1 border border-white/15 bg-white/5 uppercase tracking-widest rounded-full">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="font-mono-mini text-[10px] uppercase tracking-[0.3em] text-[color:var(--ns-accent)] mb-2">
                Qualité technique
              </div>
              <div className="flex flex-wrap gap-2">
                {movie.is_4k && <TechBadge>4K Ultra HD</TechBadge>}
                {movie.is_hdr && <TechBadge>HDR</TechBadge>}
                {movie.is_dolby_atmos && <TechBadge>Dolby Atmos</TechBadge>}
              </div>
            </div>
          </aside>

          <section className="md:col-span-2 space-y-10">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl uppercase mb-4 tracking-tight">Synopsis</h2>
              <p className="text-base sm:text-lg text-zinc-200 leading-relaxed whitespace-pre-line">
                {movie.synopsis}
              </p>
            </div>

          <div className="space-y-5">
            <h2 className="font-display text-3xl uppercase tracking-tight">Commentaires & Notes</h2>

            {user && user !== false && (
              <form onSubmit={submitComment} className="space-y-3 bg-[color:var(--ns-card)] border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`comment-rating-${n}`}
                      onClick={() => setRating(n)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center border transition ${
                        n <= rating ? "bg-[color:var(--ns-accent)] border-[color:var(--ns-accent)]" : "border-white/15 bg-white/5"
                      }`}
                    >
                      <Star size={14} fill={n <= rating ? "currentColor" : "none"} />
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="comment-input"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Partagez votre avis…"
                  rows={3}
                  className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg outline-none focus:border-[color:var(--ns-accent)] transition"
                />
                <button data-testid="comment-submit" type="submit" className="px-5 py-2 btn-accent rounded-full text-sm uppercase tracking-widest">
                  Publier
                </button>
              </form>
            )}

            <div className="space-y-3">
              {comments.length === 0 && (
                <div className="text-zinc-500 text-sm">Soyez le premier à commenter.</div>
              )}
              {comments.map((c) => (
                <div key={c.id} data-testid={`comment-${c.id}`} className="bg-[color:var(--ns-card)] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="font-mono-mini text-xs uppercase tracking-widest text-white">{c.user_name}</div>
                    <div className="flex items-center gap-1 text-yellow-400 text-xs">
                      {Array.from({ length: c.rating }).map((_, i) => (
                        <Star key={`${c.id}-star-${i}`} size={11} fill="currentColor" />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-zinc-300">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function InfoTile({ icon, label, value, accent = false }) {
  return (
    <div
      className={`rounded-xl border p-4 flex flex-col gap-2 ${
        accent
          ? "border-[color:var(--ns-accent)]/40 bg-[color:var(--ns-accent)]/5"
          : "border-white/10 bg-[color:var(--ns-card)]"
      }`}
    >
      <div className="font-mono-mini text-[10px] uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-2">
        <span className="text-[color:var(--ns-accent)]">{icon}</span>
        {label}
      </div>
      <div
        className={`font-display uppercase leading-none ${
          accent ? "text-3xl text-[color:var(--ns-accent)]" : "text-xl sm:text-2xl text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function TechBadge({ children }) {
  return (
    <span className="font-mono-mini text-[10px] px-3 py-1.5 border border-[color:var(--ns-accent)] text-[color:var(--ns-accent)] uppercase tracking-widest rounded-full">
      {children}
    </span>
  );
}
